import React from 'react';
import BasicLayout from 'layouts/BasicLayout';
import { DefaultPage } from 'styles/Page.styles';
import ShopSearchHead from 'components/ShopSearchHead/ShopSearchHead';
import { GetInitialIndexPageProps } from 'models/GetInitialIndexPageProps';
import { GetServerSidePropsContext } from 'next';
import {
  determineEnvironmentFromHost,
  determineHost,
  determineTargetMarketFromHost
} from 'helpers/url-mapper';
import { getSearchResults } from 'helpers/getSearchResults';
import { TranslateFunction } from 'models/TranslateFunction';
import { useIntl } from 'react-intl';
import { getResultPageComponents } from 'helpers/getResultPageComponents';
import { getModals } from 'helpers/getModals';
import { AccountPageInputProps } from 'models/AccountPageProps';
import { FilterableQueryParams } from 'models/FilterableQueryParams';
import { getPageLinks } from 'helpers/getPageLinks';
import { emptyPage } from 'helpers/emptyPage';
import { isEmbedActive } from 'helpers/isEmbedActive';

const getInitialProps: GetInitialIndexPageProps = async (
  context: GetServerSidePropsContext
) => {
  const host = determineHost(context);
  const environment = determineEnvironmentFromHost(host);
  const targetMarket = determineTargetMarketFromHost(host);
  const props = await getSearchResults(
    context,
    environment,
    targetMarket
  ).catch((errorStatusCode) => {
    if (context?.res) {
      context.res.statusCode = errorStatusCode;
    }
    return emptyPage;
  });
  const pageLinks = await getPageLinks(context, environment);

  // We filter the pageLinks to only include the ones that match with the shop results
  const availableDomains = Array.from(
    new Set(
      props.shops?.map((shop) => {
        if (shop.profileUrl.split('/')[0].includes('trustedshops')) {
          return shop.profileUrl.split('/')[0];
        }
        return (
          shop.profileUrl.split('/')[0] + '/' + shop.profileUrl.split('/')[1]
        );
      })
    )
  );

  const filteredPageLinks = pageLinks.filter((pageLink) =>
    availableDomains.some((domain) => pageLink.url.includes(domain))
  );

  return {
    ...props,
    searchTerm: undefined,
    host,
    pageLinks: filteredPageLinks
  };
};

function AccountPage({
  shops,
  category,
  account,
  activePage,
  hasValidCertificate,
  sort,
  metadata,
  queryParams,
  pageLinks,
  setModalVisible,
  modalVisible,
  mobileMenuOpen,
  setMobileMenuOpen,
  onParameterChange,
  categoryList,
  host
}: AccountPageInputProps) {
  const { formatMessage } = useIntl();
  const translate: TranslateFunction = (id: string) => formatMessage({ id });

  const activeFilters: FilterableQueryParams = {
    account,
    page: activePage || undefined,
    sort,
    hasValidCertificate,
    category: category?.urlPath,
    ...(isEmbedActive(queryParams.embed) ? { embed: true } : {})
  };

  return (
    <BasicLayout noScrolling={!!modalVisible || mobileMenuOpen}>
      <DefaultPage $hasAside={false}>
        <ShopSearchHead
          {...{
            category,
            account: metadata.accountName,
            totalShopCount: metadata.totalShopCount,
            queryParams,
            pageLinks,
            host
          }}
        />
        {getResultPageComponents({
          activeFilters,
          onParameterChange,
          category,
          account: metadata.accountName,
          setMobileMenuOpen,
          mobileMenuOpen,
          metadata,
          setModalVisible,
          calledByAccountPage: true,
          categoryList,
          shops
        })}
      </DefaultPage>
      {getModals({
        modalVisible,
        setModalVisible,
        activeFilters,
        onParameterChange,
        translate
      })}
    </BasicLayout>
  );
}

AccountPage.getInitialProps = getInitialProps;

export default AccountPage;
